import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch, bindActionCreators } from 'redux';
import MCIRecordForm from '../../components/Records/MCI/MCIRecordForm';
import * as RecordActions from '../../actions/Records/actions';
import * as UserActions from '../../actions/User/actions';
import { RootState } from '../../store/configureStore';
import Loading from '../../components/common/Loading';
import { MCITestRecord } from '../../constants/Records';

interface RouteParams {
  institutionId: string;
  userId: string;
  timestamp: string;
}

interface StateProps {
  institution: any;
  userListData: any[];
  mci: any;
}

interface DispatchProps {
  recordActions: typeof RecordActions;
  userActions: typeof UserActions;
}

type Props = StateProps & DispatchProps;

const MCIRecordEditContainer: React.FC<Props> = ({ institution, userListData, mci, recordActions, userActions }) => {
  const { institutionId, userId, timestamp } = useParams<RouteParams>();
  const userFetchedRef = useRef(false);
  const recordFetchedRef = useRef(false);

  const user = userListData.find((u: any) => u.user_id === userId);
  const uniqueId = user?.unique_id;

  useEffect(() => {
    if (userListData.length === 0 && !userFetchedRef.current && institutionId) {
      userFetchedRef.current = true;
      userActions.getUsersWithUniqueId(institutionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, userListData.length]);

  useEffect(() => {
    if (uniqueId && timestamp && !recordFetchedRef.current) {
      recordFetchedRef.current = true;
      recordActions.fetchMCIRecord(uniqueId, decodeURIComponent(timestamp));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId, timestamp]);

  const handleFetchRecord = useCallback(
    (id: string, ts: string) => {
      recordActions.fetchMCIRecord(id, ts);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleCreate = useCallback(
    (id: string, data: Partial<MCITestRecord>) => {
      recordActions.createMCIRecord(id, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleUpdate = useCallback(
    (id: string, ts: string, data: Partial<MCITestRecord>) => {
      recordActions.updateMCIRecord(id, ts, data);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleResetFlags = useCallback(() => {
    recordActions.resetMCIFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return <Loading />;
  }

  return (
    <MCIRecordForm
      institutionId={institutionId}
      institutionName={institution?.institution_name || ''}
      userId={userId}
      userName={user.user_name}
      uniqueId={user.unique_id || ''}
      currentRecord={mci.current}
      isEdit={true}
      executing={mci.executing}
      created={mci.created}
      updated={mci.updated}
      onFetchRecord={handleFetchRecord}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onResetFlags={handleResetFlags}
    />
  );
};

function mapStateToProps(state: RootState): StateProps {
  return {
    institution: state.institution.institution,
    userListData: state.user.listData,
    mci: state.records.mci,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    recordActions: bindActionCreators(RecordActions, dispatch),
    userActions: bindActionCreators(UserActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MCIRecordEditContainer);
